import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { settingsService } from "@/services";
import type { BusinessSettings } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useBranding } from "@/context/BrandingContext";
import { canEditBusinessSettings } from "@/config/roles";
import { PageHeader } from "@/components/feature/PageHeader";
import { SettingsNav } from "../components/SettingsNav";
import { Card } from "@/components/base/Card";
import { TextField } from "@/components/base/TextField";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { ErrorState } from "@/components/base/ErrorState";
import defaultLogo from "@/assets/mk-logo.png";

const MAX_LOGO_SIZE_MB = 5;
const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];

async function optimizeLogoImage(file: File): Promise<string> {
  // SVG vector graphics don't need rasterization
  if (file.type === "image/svg+xml") {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const MAX_DIM = 600;
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(String(event.target?.result ?? ""));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        const format = file.type === "image/png" ? "image/png" : "image/jpeg";
        resolve(canvas.toDataURL(format, 0.92));
      };
      img.onerror = () => resolve(String(event.target?.result ?? ""));
      img.src = String(event.target?.result ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function BusinessSettingsPage() {
  const { role, user } = useAuth();
  const toast = useToast();
  const { updateBrandingState } = useBranding();
  const canEdit = canEditBusinessSettings(role);

  const [settings, setSettings] = useState<BusinessSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [logoInfo, setLogoInfo] = useState<{ name: string; size: string } | null>(null);

  useEffect(() => {
    let active = true;
    settingsService
      .getBusinessSettings()
      .then((s) => {
        if (active) {
          setSettings({
            ...s,
            tagline: s.tagline ?? "Kameti & Chit Fund Management Workspace",
          });
        }
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : "Could not load business settings."))
      .finally(() => active && setSettings((s) => s ?? null));
    return () => {
      active = false;
    };
  }, []);

  const patch = (field: keyof BusinessSettings, value: string) =>
    setSettings((s) => (s ? { ...s, [field]: value } : s));

  const handleLogo = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type && !ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Unsupported file format", "Please upload a PNG, JPG, WebP, or SVG image.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      toast.error(
        "File too large",
        `Selected image (${sizeMB} MB) exceeds maximum allowed size of ${MAX_LOGO_SIZE_MB} MB. Please upload a smaller image.`
      );
      e.target.value = "";
      return;
    }

    try {
      const formattedSize =
        file.size >= 1024 * 1024
          ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
          : `${Math.round(file.size / 1024)} KB`;
      setLogoInfo({ name: file.name, size: formattedSize });
      const dataUrl = await optimizeLogoImage(file);
      patch("logoUrl", dataUrl);
      toast.success("Logo loaded", `${file.name} (${formattedSize}) ready to save.`);
    } catch (err) {
      toast.error("Failed to read image", err instanceof Error ? err.message : "Please try another image.");
    } finally {
      e.target.value = "";
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!settings || saving || !canEdit) return;
    setSaving(true);
    try {
      const updated = await settingsService.updateBusinessSettings(settings, user?.name ?? "Staff");
      setSettings(updated.business);
      updateBrandingState({
        businessName: updated.business.businessName,
        tagline: updated.business.tagline,
        logoUrl: updated.business.logoUrl,
      });
      toast.success("Branding & business profile saved", "Software name, logo, and document headers have been updated everywhere.");
    } catch (err) {
      toast.error("Could not save", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const activeLogo = settings?.logoUrl || defaultLogo;
  const nameParts = (settings?.businessName || "M.K. FINANCIAL SERVICES").trim().split(" ");
  const firstName = nameParts[0] || "";
  const restName = nameParts.slice(1).join(" ");

  return (
    <div>
      <PageHeader title="Settings" subtitle="Organization configuration, staff users and the audit trail." />
      <SettingsNav />

      {error ? (
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      ) : !settings ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <form onSubmit={save} className="space-y-5 lg:col-span-2">
            {!canEdit ? (
              <Alert variant="info" title="Read-only">
                Only Admin and Super Admin can edit the software branding and business profile.
              </Alert>
            ) : null}

            {/* Software Branding & Identity */}
            <Card
              title="Software Branding & Identity"
              subtitle="Customize your company name, subtitle tagline, and software logo. Changes apply across the entire application in real time."
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <TextField
                    label="Software / Company Name"
                    value={settings.businessName}
                    onChange={(e) => patch("businessName", e.target.value)}
                    disabled={!canEdit}
                    helper="Displayed in the sidebar, header, login screen, and page title."
                    required
                  />
                  <TextField
                    label="Tagline / Subtitle"
                    value={settings.tagline ?? ""}
                    onChange={(e) => patch("tagline", e.target.value)}
                    disabled={!canEdit}
                    helper="Displayed below company name on login and vouchers."
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-700 uppercase tracking-wider mb-2">
                    Application Logo
                  </label>
                  <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center p-3.5 rounded-lg border border-background-200 bg-background-50/50">
                    <div className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-xl border border-background-200 bg-white p-1 shadow-sm">
                      <img src={activeLogo} alt="Logo preview" className="h-full w-full object-contain" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900">
                          {settings.logoUrl ? "Custom Logo Configured" : "Default Built-in Logo"}
                        </span>
                        {settings.logoUrl && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-800">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        Accepts PNG, JPG, WebP, SVG · Max file size: {MAX_LOGO_SIZE_MB} MB. Recommended square or landscape format.
                      </p>
                      {logoInfo ? (
                        <p className="inline-flex items-center gap-1 rounded bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 mt-1.5 border border-primary-200">
                          <i className="ri-file-image-line" />
                          <span>Selected: {logoInfo.name} ({logoInfo.size})</span>
                        </p>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          disabled={!canEdit}
                          icon={<i className="ri-upload-2-line" />}
                          onClick={() => document.getElementById("logo-upload")?.click()}
                        >
                          {settings.logoUrl ? "Replace logo" : "Upload custom logo"}
                        </Button>
                        {settings.logoUrl ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                            disabled={!canEdit}
                            onClick={() => {
                              patch("logoUrl", "");
                              setLogoInfo(null);
                            }}
                          >
                            Reset to default logo
                          </Button>
                        ) : null}
                        <input
                          id="logo-upload"
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={handleLogo}
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Business Details */}
            <Card title="Organization & Address" subtitle="Proprietor details and physical address printed on documents.">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <TextField
                    label="Proprietor / Contact Person"
                    value={settings.proprietor}
                    onChange={(e) => patch("proprietor", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                <div className="sm:col-span-2">
                  <TextField
                    label="Address"
                    value={settings.address}
                    onChange={(e) => patch("address", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                <TextField label="City" value={settings.city} onChange={(e) => patch("city", e.target.value)} disabled={!canEdit} />
                <TextField label="State" value={settings.state} onChange={(e) => patch("state", e.target.value)} disabled={!canEdit} />
                <TextField label="PIN code" value={settings.pinCode} onChange={(e) => patch("pinCode", e.target.value)} disabled={!canEdit} />
              </div>
            </Card>

            {/* Contact & Registration */}
            <Card title="Contact & Tax Registration" subtitle="Communication details and GST/Registration number.">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField label="Primary phone" value={settings.primaryPhone} onChange={(e) => patch("primaryPhone", e.target.value)} disabled={!canEdit} />
                <TextField label="Alternate phone" value={settings.alternatePhone} onChange={(e) => patch("alternatePhone", e.target.value)} disabled={!canEdit} />
                <TextField label="Email" type="email" value={settings.email} onChange={(e) => patch("email", e.target.value)} disabled={!canEdit} />
                <TextField label="Website" value={settings.website} onChange={(e) => patch("website", e.target.value)} disabled={!canEdit} />
                <div className="sm:col-span-2">
                  <TextField
                    label="Registration / GST number"
                    value={settings.registrationNumber}
                    onChange={(e) => patch("registrationNumber", e.target.value)}
                    disabled={!canEdit}
                    helper="Optional — printed on official receipts and payout vouchers."
                  />
                </div>
              </div>
            </Card>

            {canEdit ? (
              <div className="flex justify-end pt-2">
                <Button type="submit" loading={saving} icon={<i className="ri-save-line" />}>
                  Save Branding & Profile
                </Button>
              </div>
            ) : null}
          </form>

          {/* Live Preview Sidecards */}
          <div className="space-y-4">
            {/* Sidebar Branding Preview */}
            <Card title="Sidebar Brand Preview" subtitle="How your brand appears in the main navigation.">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 shadow-inner">
                <div className="flex items-center gap-2.5 bg-background-50 py-2 px-3 rounded-md border border-background-100">
                  <img src={activeLogo} alt="Logo" className="h-7 w-7 object-contain rounded" />
                  <span className="text-sm font-bold tracking-tight text-foreground-900 leading-tight truncate">
                    {firstName} {restName && <span className="text-primary-600">{restName}</span>}
                  </span>
                </div>
                {settings.tagline && (
                  <p className="mt-2 text-xs text-foreground-500 px-3 truncate">
                    {settings.tagline}
                  </p>
                )}
              </div>
            </Card>

            {/* Document Header Preview */}
            <Card title="Printed Document Preview" subtitle="How the printed header appears on receipts and vouchers.">
              <div className="rounded-md border-2 border-background-200 bg-background-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <img src={activeLogo} alt="Logo" className="mb-2 h-10 w-10 object-contain rounded" />
                    <p className="text-base font-bold text-foreground-950">{settings.businessName || "Business name"}</p>
                    <p className="mt-0.5 text-xs text-foreground-600">
                      {settings.address || "Office Address"}, {settings.city} {settings.state} {settings.pinCode ? `– ${settings.pinCode}` : ""}
                    </p>
                    {settings.primaryPhone && <p className="mt-0.5 text-xs text-foreground-600">{settings.primaryPhone}</p>}
                    {settings.email && <p className="mt-0.5 text-xs text-foreground-600">{settings.email}</p>}
                    {settings.registrationNumber ? (
                      <p className="mt-1 text-[11px] font-medium text-foreground-500">{settings.registrationNumber}</p>
                    ) : null}
                  </div>
                  <div className="text-right text-[11px] text-foreground-500">
                    <p className="text-xs font-semibold text-foreground-700">Receipt</p>
                    <p>No. RCPT01042</p>
                    <p>25-08-2026</p>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                This branding is synchronized across the entire system. Saving updates the sidebar navigation, login page, vouchers, receipts, and reports in real time without refreshing.
              </p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}